/**
 * Getting back into a session you were already in, for as long as it is worth trying.
 *
 * A dropped guest and a host that has gone for good look identical from a guest's chair: in both
 * cases the connection stopped and nothing is arriving. Waiting is what tells them apart. If the
 * host is still there, joining the same code again finds it and the seat it kept; if the host has
 * really gone, every attempt comes back saying nobody hosts that code, and once the budget is spent
 * the guest gets the definitive answer instead of a guess. Nothing has to decide which it was.
 *
 * The budget is the same constant the host holds the seat for, so the two cannot drift into a guest
 * still dialling a chair somebody else has already been given.
 *
 * Retrying is only worth it for failures that could change. A session that is full is full on the
 * next attempt too, and a peer on a different protocol version will be on a different protocol
 * version in a second, so both stop the loop with the answer they gave rather than spending the
 * whole budget rediscovering it.
 *
 * The clock and the wait arrive as arguments, so the whole loop runs in a test in microseconds.
 */

import { asNetError, NetError } from '@/net/session/sessionErrors';
import type { NetErrorCode } from '@/net/session/sessionErrors';
import type { Transport } from '@/net/transportContract';

/** Long enough not to hammer a relay, short enough to catch a wifi blip inside the budget. */
const RETRY_GAP_MS = 1500;

/** Failures another attempt cannot fix. Everything else is worth trying again. */
const HOPELESS: ReadonlySet<NetErrorCode> = new Set<NetErrorCode>([
  'sessionFull',
  'versionMismatch',
  'invalidCode',
  'codeAlreadyHosted',
]);

export interface RejoinAttemptOptions {
  readonly budgetMs: number;
  /** One attempt at the same code. Rejects with a `NetError` the way opening always does. */
  readonly open: () => Promise<Transport>;
  readonly nowMs: () => number;
  readonly sleep: (ms: number) => Promise<void>;
  /** Cancelled by a player who stopped waiting, or by the screen coming down. */
  readonly signal: AbortSignal;
  /** How much of the budget is left, for a countdown. Called before every attempt. */
  readonly onWaiting: (msLeft: number) => void;
}

export type RejoinResult =
  | { readonly ok: true; readonly transport: Transport }
  | { readonly ok: false; readonly error: NetError };

export async function runRejoinAttempt(options: RejoinAttemptOptions): Promise<RejoinResult> {
  const deadline = options.nowMs() + options.budgetMs;

  for (;;) {
    if (options.signal.aborted) {
      return { ok: false, error: new NetError('sessionClosed', 'stopped waiting') };
    }

    options.onWaiting(Math.max(deadline - options.nowMs(), 0));

    try {
      return { ok: true, transport: await options.open() };
    } catch (cause) {
      const error = asNetError(cause, 'peerConnectionFailed');
      if (HOPELESS.has(error.code)) return { ok: false, error };
    }

    // Measured after the attempt, because an attempt that ran the whole budget out on its own has
    // already answered the question and there is nothing left to spend on another.
    if (options.nowMs() + RETRY_GAP_MS >= deadline) {
      return { ok: false, error: new NetError('hostLeft', 'the session did not come back') };
    }
    await options.sleep(RETRY_GAP_MS);
  }
}
